import {DatePipe, DecimalPipe, Location } from '@angular/common';
import {Component, OnInit, ElementRef} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {BaseCRUDComponent} from 'app/core-ui/abstract/basegrid/baseCRUD.component';
import {ConfigService} from 'app/core-ui/abstract/services/config.service';
import {EventsEmitterService} from 'app/core-ui/abstract/services/emitter.service';
import {LogService} from 'app/core-ui/abstract/services/log.service';
import {VisualsService} from 'app/core-ui/abstract/services/visuals.service';
import {
  maxValueValidator,
  minValueValidator,
} from 'app/core-ui/dynamic-form/numericValueValidator.component';
import {Observable, throwError, forkJoin } from 'rxjs';
import {AuthFlowSetupService} from './authFlowSetup.service';

@Component({
  selector: 'authFlowSetupEdit-grid',
  templateUrl: './authFlowSetupEdit.component.html',
    styleUrls: ['./authFlowSetupEdit.component.css'],
  providers: [AuthFlowSetupService, DecimalPipe, DatePipe],
})
export class AuthFlowSetupEditComponent
  extends BaseCRUDComponent<AuthFlowSetupService>
  implements OnInit
{
  public formErrors = {};
  public submitError = null;

  public lookups: any[] = [];

  public minAuthCount = 0;

  customCurrencyConfig = {};

  public minAmount: number = 0;
  public maxAmount: number = 999999999;
  fourEyes: boolean = false;
  fourEyesRequestId: string = '';
  foureyesComparisonId: number;
  differences: any;
  tagName: string;
  isCollapsed: boolean = true;
  isUpdating = false;
  verifierList: any[] = [];
  approverList: any[] = [];
  leftGroupsMapped: any[] = [];
  rightGroupsMapped: any[] = [];
  leftSequenceMapped: any[] = [];
  rightSequenceMapped: any[] = [];

  constructor(
    private service: AuthFlowSetupService,
    private datePipe: DatePipe,
    private location: Location,
    public eventsEmitterService: EventsEmitterService,
    fb: UntypedFormBuilder,
    logger: LogService,
    visualsService: VisualsService,
    route: ActivatedRoute,
    router: Router,
    public config: ConfigService,
    elem: ElementRef
  ) {
    super(logger, visualsService, route, fb, router);
    this.tagName = elem.nativeElement.tagName.toLowerCase();

    this.fillView();
    this.eventsEmitterService.languageChange.subscribe({
      next: (langId) => {
        this.loadVisuals();
        this.fillView();
      },
    });
  }

  ngOnInit() {
    this.loadVisuals();
    this.urlParams = {};

    this.route.queryParams.subscribe({
      next: (params) => {
        if (params['fourEyes']) {
          const cmpId = params['cmpId'];
          if (cmpId) {
            forkJoin({
              verifiers: this.service.getAsyncCompanyVerifiers(cmpId),
              approvers: this.service.getAsyncCompanyApprovers(cmpId)
            }).subscribe(({ verifiers, approvers }) => {
              this.verifierList = verifiers;
              this.approverList = approvers;

              this.fourEyes = true;
              this.fourEyesRequestId = params['requestId'];
              this.foureyesComparisonId = params['foureyesComparisonId'];
              this.isUpdating = true;

              this.getFourEyesRequest();
            });
          }
        } else {
          this.urlParams = this.cleanParams(params);

          this.logger.warn(params);
          this.fillView();

          if (this.loadBean(this.urlParams) != null) {
            this.loading = true;
          }
        }
      },
      error: (error) => {
        return throwError(() => new Error(error));
      },
    });
  }

  private cleanParams(params: any): any {
    const { actionCd, cmpId } = params;
    return { actionCd, cmpId };
  }

  private getFourEyesRequest() {
    this.service.get4eyesRequest(this.fourEyesRequestId).subscribe({
      next: (data) => {
        this.bean = JSON.parse(data.data.oldJsonRec);
        this.differences = data.data.differences;
        this.loading = false;
        this.fourEyes = true;

        if (!this.differences.hasOwnProperty('flowPolicy')) {
        this.differences.flowPolicy = {
          leftValue: this.bean.flowPolicy,
          rightValue: this.bean.flowPolicy
        };
      }
        
        this.leftGroupsMapped = this.mapMembers(
          this.differences.policyConfig.leftValue.groups,
          this.approverList
        );

        this.rightGroupsMapped = this.mapMembers(
          this.differences.policyConfig.rightValue.groups,
          this.approverList
        );

        this.leftSequenceMapped = this.mapSequenceWithNames(
          this.differences.policyConfig.leftValue.sequence,
          this.verifierList,
          this.approverList
        );

        this.rightSequenceMapped = this.mapSequenceWithNames(
          this.differences.policyConfig.rightValue.sequence,
          this.verifierList,
          this.approverList
        );

        this.buildForm();
      },
      error: (error) => {
        this.logger.info(error);
        this.loading = false;
      },
    });
  }


  public getGenericVisuals() {
    this.genericVisuals = this.visualsService.getFromStorageAsJSON('visualsArray')['generic'];
  }

  public fillView() {
    this.lookups['actionList'] = this.visualsService.getLkpFromStorageAsJSON(
      'LKP_SEC_TESLA_FLOW_S_ACTION_CD',
      true,
    );
    this.lookups['policyList'] = this.visualsService.getLkpFromStorageAsJSON(
      'LKP_SEC_TESLA_FLOW_S_FLOW_POLICY',
      true,
    );
    this.lookups['verifierList'] = this.service.getAsyncCompanyVerifiers(this.urlParams['cmpId']);
    this.lookups['approverList'] = this.service.getAsyncCompanyApprovers(this.urlParams['cmpId']);
    this.lookups['logicalConditions'] = this.visualsService.getLkpFromStorageAsJSON('LKP_SEC_TESLA_FLOW_LOGIC_COND', true);
  }

  public loadBean(map: any) {
    this.loading = true;
    this.setCustomCurrencyConfig();
    this.service.loadBean(this.urlParams['actionCd'], this.urlParams['cmpId']).subscribe({
      next: (data) => {
        this.logger.log(data);
        this.bean = data;
        this.getValidationMessages();
        if (!this.fourEyes) {
          this.buildForm();
        }
        this.loading = false;
      },
      error: (error) => {
        this.logger.log(error);
        this.router.navigateByUrl('menu/7213');
      },
    });
  }

  public save(bean: any) {
    this.submiting = true;

    let formValues = this.editForm.value;
    formValues['cmpId'] = this.urlParams['cmpId'];
    bean = {...formValues, langId: localStorage.getItem('langId')};

    this.service.save(bean).subscribe({
      next: (response) => {
        if (response.success) {
          LogService.emitAlert({
            type: 'success',
            body: response.message,
          });
          this.router.navigateByUrl('menu/7213');
        } else {
          LogService.emitAlert({
            type: 'error',
            body: response.message,
          });
        }
        this.submiting = false;
      },
      error: (error) => {
        this.submiting = false;
        return throwError(() => new Error(error));
      },
    });
  }

  public backToGrid() {
    if (this.fourEyes) {
      if (window.history.length > 1) {
        this.location.back();
        this.fourEyes = false;
      } else {
        this.router.navigateByUrl('menu/home');
      }
    } else {
      this.router.navigateByUrl('menu/7213');
    }
  }

  public buildForm() {
    switch (this.bean.flowPolicy) {
      case 'SEQUENTIAL':
        this.minAuthCount = 2;
        break;
      default:
        this.minAuthCount = 0;
        break;
    }

    this.editForm = this.fb.group({
      // flow data
      actionCd: [this.bean.actionCd, []],
      cmpId: [this.bean.cmpId, []],
      authCount: [{value:this.bean.authCount, disabled: this.fourEyes}, [Validators.required, maxValueValidator(10)]],
      flowPolicy: [{value:this.bean.flowPolicy ? String(this.bean.flowPolicy) : null, disabled: this.fourEyes}, []],
      username: [{value:this.bean.username, disabled: this.fourEyes}, []],
      timestamp: [{value:this.bean.timestamp, disabled: this.fourEyes}, []],

      // policy configuration
      policyConfig: this.initPolicyConfig(),
    });

    // init form errors
    let kcontrols: any = Object.keys(this.editForm.controls);
    let controls: any = Object.assign({}, this.editForm.controls);

    kcontrols.forEach((element) => {
      if (controls[element] instanceof UntypedFormArray) {
        let formArray = [];
        controls[element].controls.forEach((ctrl) => {
          let arrkeys: any = Object.keys(ctrl.controls);
          let innerCtrls = {};
          arrkeys.forEach((k) => {
            innerCtrls[k] = '';
          });
          formArray.push(innerCtrls);
        });

        controls[element.toString()] = formArray;
      } else {
        controls[element] = '';
      }
    });
    this.formErrors = controls;

    this.editForm.valueChanges.subscribe({
      next: (data) => this.onValueChanged(data),
    });
  }

  initPolicyConfig() {
    let fbgroup = this.fb.group({
      table: [this.bean.policyConfig.table, []],
      sequence: this.initSequence(),
      tiers: this.initTiers(),
      groups: this.initGroups(),  
      'basedOn': [
        this.bean.flowPolicy == 'TIERING'
          ? this.bean.policyConfig.basedOn == null
            ? 'sum'
            : this.bean.policyConfig.basedOn
          : null,
        [],
      ],
      'tierHierarchy': [this.bean.flowPolicy == 'TIERING' ? this.bean.policyConfig.tierHierarchy : 'sequential', []],
    });

    return fbgroup;
  }

  //sequence fns
  initSequence() {
    let fbarray = this.fb.array([]);
    if (this.bean.policyConfig.sequence) {
      this.bean.policyConfig.sequence.forEach((element) => {
        fbarray.push(this.sequenceGroup(element));
      });
    }

    return fbarray;
  }

  sequenceGroup(element?: any) {
    if (!element) {
      this.bean.policyConfig.sequence.push({});
      element = this.bean.policyConfig.sequence[this.bean.policyConfig.sequence.length - 1];
    }

    return this.fb.group({
      rprId: [element.rprId ? String(element.rprId) : null, [Validators.required]],
    });
  }

  addSequence() {
    const control = <UntypedFormArray>(
      (<UntypedFormGroup>this.editForm.get('policyConfig')).controls['sequence']
    );
    control.push(this.sequenceGroup());
  }

  removeSequence(i: number) {
    const control = <UntypedFormArray>(
      (<UntypedFormGroup>this.editForm.get('policyConfig')).controls['sequence']
    );
    control.removeAt(i);
  }

  //tiers fns
  initTiers() {
    let fbarray = this.fb.array([]);

    if (this.bean.policyConfig.tiers) {
      this.bean.policyConfig.tiers.forEach((element) => {
        fbarray.push(this.tiersGroup(element));
      });
    }

    return fbarray;
  }

  tiersGroup(element?: any) {
    return this.fb.group({
      minAmount: [element.minAmount, [minValueValidator(0), maxValueValidator(999999999.99)]],
      maxAmount: [element.maxAmount, [minValueValidator(0), maxValueValidator(999999999.99)]],
      workflow: this.initWorkflow(element.workflow),
    });
  }

  addTiers() {
    const control = <UntypedFormArray>(
      (<UntypedFormGroup>this.editForm.get('policyConfig')).controls['tiers']
    );
    let tiers = this.editForm.value.policyConfig.tiers;
    let index = control.value.length - 1;
    let amount = tiers[index].minAmount + 1;
    control.push(
      this.tiersGroup({
        minAmount: tiers[index].minAmount,
        maxAmount: amount,
        workflow: tiers[index].workflow,
      }),
    );
    control.removeAt(index);

    control.push(
      this.tiersGroup({
        minAmount: amount + 1,
        maxAmount: this.maxAmount,
        workflow: [],
      }),
    );
  }

  removeTiers(i: number) {
    const control = <UntypedFormArray>(
      (<UntypedFormGroup>this.editForm.get('policyConfig')).controls['tiers']
    );
    control.removeAt(i);
    let tiers = this.editForm.value.policyConfig.tiers;
    let index = control.value.length;
    if (i == index) {
      control.push(
        this.tiersGroup({
          minAmount: tiers[index - 1].minAmount,
          maxAmount: this.maxAmount,
          workflow: tiers[index - 1].workflow,
        }),
      );
      control.removeAt(index - 1);
    } else {
      tiers[i].minAmount = tiers[i - 1].maxAmount + 1;
      this.refreshTiers();
    }
  }

  minAmountChanged(i: number) {
    let minAmount = this.editForm.value.policyConfig.tiers[i].minAmount;
    if (minAmount) {
      let length = this.editForm.value.policyConfig.tiers.length;
      minAmount = minAmount > 0 ? minAmount : minAmount * -1;
      minAmount =
        minAmount <= this.maxAmount - 2 * (length - (i + 1)) - 1
          ? minAmount
          : this.maxAmount - 2 * (length - (i + 1)) - 1;
      this.editForm.value.policyConfig.tiers[i].minAmount = minAmount;

      this.editForm.value.policyConfig.tiers[i - 1].maxAmount = minAmount - 1;
      for (let j = i; j < length - 1; j++) {
        if (minAmount >= this.editForm.value.policyConfig.tiers[j].maxAmount) {
          this.editForm.value.policyConfig.tiers[j].maxAmount = minAmount + 1;
          this.editForm.value.policyConfig.tiers[j + 1].minAmount =
            this.editForm.value.policyConfig.tiers[j].maxAmount + 1;
          minAmount = this.editForm.value.policyConfig.tiers[j + 1].minAmount;
        }
      }
      for (let k = i - 1; k > 0; k--) {
        if (
          this.editForm.value.policyConfig.tiers[k].minAmount >=
          this.editForm.value.policyConfig.tiers[k].maxAmount
        ) {
          this.editForm.value.policyConfig.tiers[k].minAmount =
            this.editForm.value.policyConfig.tiers[k].maxAmount <= 0
              ? 0
              : this.editForm.value.policyConfig.tiers[k].maxAmount - 1;
          this.editForm.value.policyConfig.tiers[k - 1].maxAmount =
            this.editForm.value.policyConfig.tiers[k].minAmount <= 0
              ? 0
              : this.editForm.value.policyConfig.tiers[k].minAmount - 1;
        }
      }
      this.refreshTiers();
    }
  }

  maxAmountChanged(i: number) {
    let maxAmount = this.editForm.value.policyConfig.tiers[i].maxAmount;
    if (maxAmount) {
      let length = this.editForm.value.policyConfig.tiers.length;
      maxAmount = maxAmount > 0 ? maxAmount : maxAmount * -1;
      maxAmount =
        maxAmount <= this.maxAmount - 2 * (length - (i + 1))
          ? maxAmount
          : this.maxAmount - 2 * (length - (i + 1));
      this.editForm.value.policyConfig.tiers[i].maxAmount = maxAmount;

      this.editForm.value.policyConfig.tiers[i + 1].minAmount = maxAmount + 1;
      for (let j = i + 1; j < length - 1; j++) {
        if (
          this.editForm.value.policyConfig.tiers[j].minAmount >=
          this.editForm.value.policyConfig.tiers[j].maxAmount
        ) {
          this.editForm.value.policyConfig.tiers[j].maxAmount =
            this.editForm.value.policyConfig.tiers[j].minAmount + 1;
          this.editForm.value.policyConfig.tiers[j + 1].minAmount =
            this.editForm.value.policyConfig.tiers[j].maxAmount + 1;
        }
      }
      for (let k = i; k > 0; k--) {
        if (maxAmount <= this.editForm.value.policyConfig.tiers[k].minAmount) {
          this.editForm.value.policyConfig.tiers[k].minAmount = maxAmount <= 0 ? 0 : maxAmount - 1;
          this.editForm.value.policyConfig.tiers[k - 1].maxAmount =
            this.editForm.value.policyConfig.tiers[k].minAmount <= 0
              ? 0
              : this.editForm.value.policyConfig.tiers[k].minAmount - 1;
          maxAmount = this.editForm.value.policyConfig.tiers[k - 1].maxAmount;
        }
      }
      this.refreshTiers();
    }
  }

  public refreshTiers() {
    const control = <UntypedFormArray>(
      (<UntypedFormGroup>this.editForm.get('policyConfig')).controls['tiers']
    );
    let tiers = this.editForm.value.policyConfig.tiers;
    const length = tiers.length;

    for (let i = 0; i < length; i++) {
      control.push(this.tiersGroup(tiers[i]));
    }
    for (let i = 0; i < length; i++) {
      control.removeAt(0);
    }
  }

  //workflow fns
  initWorkflow(workflow: any) {
    let fbarray = this.fb.array([]);

    if (workflow) {
      workflow.forEach((element) => {
        fbarray.push(this.workflowGroup(element));
      });
    }

    return fbarray;
  }

  workflowGroup(element?: any) {
    return this.fb.group({
      groupId: [element.groupId, []],
      count: [element.count, []],
      rule: [element.rule, []]
    });
  }

  addWorkflow(element?: any) {
    const control = <UntypedFormArray>(<UntypedFormGroup>element).controls['workflow'];
    control.push(this.workflowGroup(element));
  }

  removeWorkflow(element: any, i: number) {
    const control = <UntypedFormArray>(<UntypedFormGroup>element).controls['workflow'];
    control.removeAt(i);
  }

  //groups fns
  initGroups() {
    let fbarray = this.fb.array([]);

    if (this.bean.policyConfig.groups) {
      this.bean.policyConfig.groups.forEach((element) => {
        fbarray.push(this.groupsGroup(element));
      });
    }

    return fbarray;
  }

  groupsGroup(element?: any) {
    if (!element) {
      this.bean.policyConfig.groups.push({});
      element = this.bean.policyConfig.groups[this.bean.policyConfig.groups.length - 1];
    }

    return this.fb.group({
      groupId: [element.groupId, []],
      groupVisual: [element.groupVisual, []],
      members: [element.members ? element.members.map((x) => String(x)) : [], []],
    });
  }

  addGroups() {
    const control = <UntypedFormArray>(
      (<UntypedFormGroup>this.editForm.get('policyConfig')).controls['groups']
    );
    control.push(
      this.groupsGroup({
        groupId: control.length + 1,
        groupVisual: null,
        members: [],
      }),
    );
    this.calcGroupItems();
  }

  removeGroups(i: number) {
    const control = <UntypedFormArray>(
      (<UntypedFormGroup>this.editForm.get('policyConfig')).controls['groups']
    );
    control.removeAt(i);
    this.calcGroupItems();
  }

  calcGroupItems() {
    this.editForm.controls['policyConfig']['controls']['groups'].controls = [
      ...this.editForm.controls['policyConfig']['controls']['groups'].controls,
    ];
  }

  public getValidationMessages() {
    // Use "this.genericVisuals.genericerror" for a generic message
    this.validationMessages = {
      actionCd: {pattern: this.genericVisuals.interror},
      authCount: {
        required: this.genericVisuals.required,
        maxValue: 'Maximun allowed value exceeded',
      },
      flowPolicy: {pattern: this.genericVisuals.interror},
      username: {pattern: this.genericVisuals.interror},
      timestamp: {pattern: this.genericVisuals.interror},

      rprId: {pattern: this.genericVisuals.required.text},

      minAmount: {},
      maxAmount: {},
      workflow: {pattern: this.genericVisuals.interror},

      count: {},

      groupId: {pattern: this.genericVisuals.interror},
      groupVisual: {pattern: this.genericVisuals.interror},
      members: {pattern: this.genericVisuals.interror},
    };
  }

  onValueChanged(data?: any) {
    if (!this.editForm) {
      return;
    }
    const form = this.editForm;
    let controls: any = Object.assign({}, this.editForm.controls);

    for (const field in this.editForm.controls) {
      if (controls[field] instanceof UntypedFormArray) {
        controls[field].controls.forEach((ctrl, i) => {
          let arrkeys: any = Object.keys(ctrl.controls);
          arrkeys.forEach((k) => {
            let control = ctrl.controls[k];
            // clear previous error message (if any)
            this.formErrors[field][i][k] = '';
            if (control && control.dirty && !control.valid) {
              const messages = this.validationMessages[k];
              for (const key in control.errors) {
                this.formErrors[field][i][k] += messages[key] + ' ';
              }
            }
          });
        });
      } else {
        // clear previous error message (if any)
        this.formErrors[field] = '';
        const control = form.get(field);
        if (control && control.dirty && !control.valid) {
          const messages = this.validationMessages[field];
          for (const key in control.errors) {
            this.formErrors[field] += messages[key] + ' ';
          }
        }
      }
    }
  }

  /**
   * ngx-select (module Output)
   * Option Selected
   */
  setValueSelected(name, value) {
    if (name == 'flowPolicy' && !!value) {
      if (value['id'] == 'BASIC') {
        this.minAuthCount = 0;
        this.editForm.get('authCount').setValue(1);
      } else if (value['id'] == 'SEQUENTIAL') {
        this.minAuthCount = 2;
        this.editForm.get('authCount').setValue(2);
        this.authPolicyCountUpdate();
      } else if (value['id'] == 'TIERING') {
        this.minAuthCount = 0;
        this.editForm.get('authCount').setValue(0);
      }
    }
  }

  authPolicyCountUpdate() {
    let authCnt = this.editForm.controls['authCount'].value;
    let oldCnt = (<UntypedFormArray>(
      (<UntypedFormGroup>this.editForm.get('policyConfig')).controls['sequence']
    )).length;
    let diff: number = Number(authCnt) - oldCnt;
    let abcDiff = Math.abs(diff);
    let signDiff = Math.sign(diff);

    for (let index = 0; index < abcDiff; index++) {
      if (signDiff > 0) {
        this.addSequence();
      } else if (signDiff < 0) {
        let removeAt = (<UntypedFormArray>(
          (<UntypedFormGroup>this.editForm.get('policyConfig')).controls['sequence']
        )).length;
        this.removeSequence(removeAt - 1);
      }
    }
  }

  nextButtonDisabled() {
    let nextButtonDisabled = false;
    if (this.editForm.value['flowPolicy'] == 'SEQUENTIAL') {
      this.editForm.controls['policyConfig']['controls']['sequence'].controls.forEach((c) => {
        nextButtonDisabled = !c.value.rprId;
      });
    }
    if (this.editForm.value['flowPolicy'] == 'TIERING') {
      this.editForm.controls['policyConfig']['controls']['groups'].controls.forEach((group) => {
        nextButtonDisabled =
          !group.value.members || group.value.members.length == 0 || !group.value.groupVisual;
      });
      this.editForm.controls['policyConfig']['controls']['tiers'].controls.forEach((tiers) => {
        if (nextButtonDisabled) {
          return true;
        }
        nextButtonDisabled =
          tiers.value.minAmount == null ||
          !tiers.value.maxAmount ||
          tiers.value.workflow.length == 0;
        tiers.controls['workflow'].controls.forEach((wf) => {
          if (nextButtonDisabled) {
            return true;
          }
          nextButtonDisabled = !wf.controls.count.value || !wf.controls.groupId.value;
        });
      });
    }
    return nextButtonDisabled;
  }

  minAuthCountExceeded() {
    if (this.editForm.value['flowPolicy'] == 'SEQUENTIAL') {
      return (
        this.editForm['controls']['authCount'].value == 0 ||
        this.editForm['controls']['authCount'].value == 1
      );
    }
    return false;
  }

  getSelectedActionVisual() {
    let selectedAction = this.lookups['actionList'].filter((a) => a.id == this.bean.actionCd)[0];
    let label = selectedAction ? selectedAction.text : this.bean.actionCd;
    return label;
  }

  public setCustomCurrencyConfig() {
    this.customCurrencyConfig = {precision: 0};
    if (this.config.getLocale() == 'el-GR') {
      Object.assign(this.customCurrencyConfig, {
        decimal: ',',
        thousands: '.',
      });
    } else if (this.config.getLocale() == 'ar-AE') {
      Object.assign(this.customCurrencyConfig, {
        decimal: '.',
        thousands: ',',
      });
    } else if (this.config.getLocale() == 'en-GB') {
      Object.assign(this.customCurrencyConfig, {
        decimal: '.',
        thousands: ',',
      });
    } else if (this.config.getLocale() == 'de-DE') {
      Object.assign(this.customCurrencyConfig, {
        decimal: ',',
        thousands: '.',
      });
    }
  }

  mapMembers(groups: any[], approverList: any[]): any[] {
    return groups.map(group => ({
      ...group,
      members: (group.members || []).map((id: string) => ({
        id,
        name: approverList.find(u => u.id == id)?.text || `Unknown (${id})`
      }))
    }));
  }

  mapSequenceWithNames(sequence: any[], verifierList: any[], approverList: any[]) {
    return sequence.map((item, index) => {
      const isLast = index === sequence.length - 1;
      const listToUse = isLast ? approverList : verifierList;
      const user = listToUse.find(u => String(u.id) == String(item.rprId));
      console.log('Looking for rprId', item.rprId, 'in list', listToUse);
      console.log('Found user:', user);
      return {
        rprId: item.rprId,
        name: user ? user.text : `Unknown (${item.rprId})`
      };
    });
  }

  hasRecFileAction(actions: any[]): boolean {
    return Array.isArray(actions) && actions.some(a => typeof a.actionCd === 'string' && a.actionCd.includes('REC_FILE'));
  }

  getGroupVisualLeftValue(groupId: string): string {
    const group = this.differences?.policyConfig?.leftValue?.groups
      ?.find(g => g.groupId === groupId);
    return group?.groupVisual || groupId;
  }

  getGroupVisualRightValue(groupId: string): string {
    const group = this.differences?.policyConfig?.rightValue?.groups
      ?.find(g => g.groupId === groupId);
    return group?.groupVisual || groupId;
  }

}
